import React, { useMemo } from 'react';
import { Box, Button, FormControl, FormLabel, Grid, Input, Select } from '@chakra-ui/react';
import { FiRefreshCw } from 'react-icons/fi';
import { SaktiEstateOption, SaktiFilterOptions, SaktiPushOdooFilters } from '../types';
import { SAKTI_UI_SIZE } from '../uiSizes';

interface SaktiPushOdooFilterBarProps {
  filters: SaktiPushOdooFilters;
  options: SaktiFilterOptions;
  onFiltersChange: (next: SaktiPushOdooFilters) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  isDisabled?: boolean;
}

const SaktiPushOdooFilterBar: React.FC<SaktiPushOdooFilterBarProps> = ({
  filters,
  options,
  onFiltersChange,
  onSubmit,
  isSubmitting = false,
  isDisabled = false,
}) => {
  const filteredEstates = useMemo(() => {
    if (!filters.company_id) {
      return options.estates;
    }
    return options.estates.filter((estate) => estate.company_id === filters.company_id);
  }, [filters.company_id, options.estates]);

  const updateFilter = (changes: Partial<SaktiPushOdooFilters>) => {
    onFiltersChange({ ...filters, ...changes });
  };

  return (
    <Box
      bg="white"
      borderRadius={SAKTI_UI_SIZE.cardRadius}
      p={SAKTI_UI_SIZE.cardPadding}
      borderWidth="1px"
      borderColor="gray.200"
    >
      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: '1fr 1fr 1fr 1fr auto' }}
        gap={SAKTI_UI_SIZE.filterGridGap}
      >
        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            COMPANY
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={filters.company_id ?? ''}
            placeholder="Semua company"
            onChange={(event) =>
              updateFilter({
                company_id: event.target.value ? Number(event.target.value) : undefined,
                estate_id: undefined,
              })
            }
          >
            {options.companies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            ESTATE
          </FormLabel>
          <Select
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            value={filters.estate_id ?? ''}
            placeholder="Pilih estate"
            onChange={(event) =>
              updateFilter({
                estate_id: event.target.value ? Number(event.target.value) : undefined,
              })
            }
          >
            {filteredEstates.map((estate: SaktiEstateOption) => (
              <option key={estate.id} value={estate.odoo_id}>
                {estate.name}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            TANGGAL MULAI
          </FormLabel>
          <Input
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            type="date"
            value={filters.date_start}
            max={filters.date_end || undefined}
            onChange={(event) => updateFilter({ date_start: event.target.value })}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize={SAKTI_UI_SIZE.filterLabel} color="gray.600" mb={1}>
            TANGGAL AKHIR
          </FormLabel>
          <Input
            size="sm"
            h={SAKTI_UI_SIZE.controlHeight}
            borderRadius={SAKTI_UI_SIZE.cardRadius}
            type="date"
            value={filters.date_end}
            min={filters.date_start || undefined}
            onChange={(event) => updateFilter({ date_end: event.target.value })}
          />
        </FormControl>

        <Button
          alignSelf="end"
          size="sm"
          h={SAKTI_UI_SIZE.controlHeight}
          w={{ base: 'full', xl: 'auto' }}
          px={5}
          leftIcon={<FiRefreshCw />}
          isLoading={isSubmitting}
          loadingText="Memproses"
          isDisabled={isDisabled || !filters.estate_id}
          onClick={onSubmit}
          bg="customGreen.500"
          _hover={{ bg: 'customGreen.600' }}
          color="white"
          mt={{ base: 2, xl: 0 }}
        >
          Jalankan Sync
        </Button>
      </Grid>
    </Box>
  );
};

export default SaktiPushOdooFilterBar;
